import { IClient, ISessionAttendee } from "intake_shared/src/types";
import { isClientMinor } from "./age";
import { buildEmptySessionAttendee } from "./session_attendee_consent";

const requiredMinorGuardianFields: {
    field: keyof IClient;
    label: string;
}[] = [
    {
        field: "primary_parent",
        label: "primary parent/guardian first name",
    },
    {
        field: "primary_parent_last_name",
        label: "primary parent/guardian last name",
    },
    {
        field: "email",
        label: "client email",
    },
    {
        field: "phone_no",
        label: "client phone",
    },
];

const requiredOtherGuardianFields: {
    field: keyof IClient;
    label: string;
}[] = [
    {
        field: "other_parent",
        label: "other parent/guardian first name",
    },
    {
        field: "other_parent_last_name",
        label: "other parent/guardian last name",
    },
    {
        field: "other_parent_email",
        label: "other parent/guardian email",
    },
    {
        field: "other_parent_phone",
        label: "other parent/guardian phone",
    },
];

function hasValue(value: IClient[keyof IClient]): boolean {
    return typeof value === "string" ? Boolean(value.trim()) : Boolean(value);
}

export function getGuardianFullName(firstName?: string, lastName?: string): string {
    return [firstName, lastName]
        .map((name) => name?.trim())
        .filter(Boolean)
        .join(" ");
}

export function canAddConfiguredOtherParentGuardian(
    client: IClient,
    attendees: ISessionAttendee[]
): boolean {
    return (
        isClientMinor(client.date_of_birth) &&
        !client.has_no_other_parent_guardian &&
        Boolean(client.other_parent?.trim()) &&
        !attendees.some(
            (attendee) =>
                attendee.active !== false && attendee.guardian_role === "other"
        )
    );
}

export function reconcileOtherParentConsentAttendee(
    client: IClient,
    attendees: ISessionAttendee[],
    includeGeneralAttendees = true
): ISessionAttendee[] {
    const retainedAttendees = includeGeneralAttendees
        ? attendees.filter(
              (attendee) =>
                  attendee.guardian_role !== "other" || attendee.active === false
          )
        : attendees.filter(
              (attendee) =>
                  attendee.guardian_role === "other" && attendee.active === false
          );
    const existingOtherParent = attendees.find(
        (attendee) => attendee.active !== false && attendee.guardian_role === "other"
    );
    if (existingOtherParent) {
        return [existingOtherParent, ...retainedAttendees];
    }

    if (attendees.some((attendee) => attendee.guardian_role === "other")) {
        return retainedAttendees;
    }

    if (!canAddConfiguredOtherParentGuardian(client, attendees)) {
        return retainedAttendees;
    }

    return [
        buildEmptySessionAttendee({
            first_name: client.other_parent?.trim() ?? "",
            last_name: client.other_parent_last_name?.trim() || "",
            email: client.other_parent_email?.trim() || "",
            relationship: "Parent/Guardian",
            guardian_role: "other",
        }),
        ...retainedAttendees,
    ];
}

export function getAppointmentHeldMissingFields(client: IClient): string[] {
    if (!client.date_of_birth) {
        return ["date of birth"];
    }

    if (!isClientMinor(client.date_of_birth)) {
        return [];
    }

    const missingFields = requiredMinorGuardianFields
        .filter(({ field }) => !hasValue(client[field]))
        .map(({ label }) => label);

    if (client.has_no_other_parent_guardian) {
        if (!client.no_other_parent_guardian_reason?.trim()) {
            missingFields.push("reason no other parent/guardian is listed");
        }
        return missingFields;
    }

    return [
        ...missingFields,
        ...requiredOtherGuardianFields
            .filter(({ field }) => !hasValue(client[field]))
            .map(({ label }) => label),
    ];
}

export function getAppointmentHeldValidationMessage(
    client: IClient
): string | undefined {
    const missingFields = getAppointmentHeldMissingFields(client);
    if (!missingFields.length) {
        return;
    }

    return `Cannot move client to Appointment Held. Complete the following fields: ${missingFields.join(
        ", "
    )}.`;
}

const guardianFieldMappings: Array<{
    clientField: keyof IClient;
    role: "other";
    attendeeField: "first_name" | "last_name" | "email";
}> = [
    { clientField: "other_parent", role: "other", attendeeField: "first_name" },
    {
        clientField: "other_parent_last_name",
        role: "other",
        attendeeField: "last_name",
    },
    { clientField: "other_parent_email", role: "other", attendeeField: "email" },
];

export function applyExplicitGuardianChartChangesToAttendees(
    dirtyFields: Set<keyof IClient>,
    updatedClient: IClient
): IClient {
    const changedMappings = guardianFieldMappings.filter(({ clientField }) => {
        if (!dirtyFields.has(clientField)) return false;
        return ((updatedClient[clientField] as string) ?? "").trim() !== "";
    });
    if (changedMappings.length === 0) return updatedClient;

    let hasChanges = false;
    const heldBookings = (updatedClient.held_bookings || []).map((booking) => {
        const hasActiveOtherGuardianAttendee = (
            booking.session_attendees || []
        ).some(
            (attendee) =>
                attendee.active !== false && attendee.guardian_role === "other"
        );
        if (!hasActiveOtherGuardianAttendee) return booking;

        let bookingChanged = false;
        const sessionAttendees = (booking.session_attendees || []).map(
            (attendee) => {
                if (attendee.active === false) return attendee;

                const attendeeUpdates: Partial<
                    Pick<ISessionAttendee, "first_name" | "last_name" | "email">
                > = {};

                for (const { clientField, role, attendeeField } of changedMappings) {
                    if (attendee.guardian_role !== role) continue;
                    attendeeUpdates[attendeeField] = (
                        (updatedClient[clientField] as string) ?? ""
                    ).trim();
                }

                if (Object.keys(attendeeUpdates).length === 0) return attendee;
                bookingChanged = true;
                return { ...attendee, ...attendeeUpdates };
            }
        );

        if (!bookingChanged) return booking;
        hasChanges = true;
        return { ...booking, session_attendees: sessionAttendees };
    });

    return hasChanges
        ? { ...updatedClient, held_bookings: heldBookings }
        : updatedClient;
}

export function getOtherParentGuardianClientFieldChanges(
    client: IClient,
    attendees: ISessionAttendee[]
): Partial<IClient> {
    const active = attendees.filter((attendee) => attendee.active !== false);
    const other = active.find((attendee) => attendee.guardian_role === "other");
    const changes: Partial<IClient> = {};

    if (other) {
        if (other.first_name.trim()) {
            changes.other_parent = other.first_name.trim();
        }
        if (other.last_name.trim()) {
            changes.other_parent_last_name = other.last_name.trim();
        }
        if (other.email?.trim()) {
            changes.other_parent_email = other.email.trim();
        }
    }

    return Object.fromEntries(
        Object.entries(changes).filter(
            ([field, value]) => value !== client[field as keyof IClient]
        )
    );
}
